/**
 * Plateforme multi événement e.labriquerie.net, (c) Mikaël FOURNY.
 * src/i18n/fr.publierAteliers.ts
 *
 * Messages de la PUBLICATION DES ATELIERS par leurs animateurs (2026-10-07) :
 * bandeau « pas encore visible du public » et bouton « Publier maintenant »,
 * étape « Publier maintenant ? » à la création (espace et inscription),
 * rappel sur l'accueil de l'espace animateur, relance des animateurs depuis
 * le back-office. Fusionné dans fr.ts.
 *
 * Constat de départ : 8 ateliers sur 13 restaient en brouillon en
 * production, invisibles du programme, sans que rien ne le dise clairement.
 *
 * Plateforme multi-événements : « atelier » n'est jamais écrit en dur, le
 * vocabulaire passe par les jetons {lex...}. Apostrophe typographique
 * partout (en ICU, l'apostrophe droite échappe l'accolade qui la suit).
 * Aucun tiret cadratin.
 *
 * Mikael - chef de projet - 2026-10-07
 */
export const messagesPublierAteliersFr: Record<string, string> = {
  // ── La règle, dite partout où elle sert ─────────────────────────────────
  'publierAteliers.regle':
    'Vous publiez votre {lexWorkshopBare}. L’organisation fixe ensuite l’horaire, la salle et le nombre de places.',
  'publierAteliers.reglePluriel':
    'Vous publiez vos {lexWorkshopPlural}. L’organisation fixe ensuite l’horaire, la salle et le nombre de places.',

  // ── La salle, fixée par l'organisation (lecture seule) ──────────────────
  'workshopFields.room.locked': 'L’organisation fixera la salle.',

  // ── Bandeau en tête de chaque carte ─────────────────────────────────────
  'publierAteliers.bandeau.brouillon': '{lexWorkshopDemCap} n’est pas encore visible du public.',
  'publierAteliers.bandeau.brouillonDetail':
    'Tant qu’{lexWorkshopPronoun} reste en brouillon, {lexWorkshopPronoun} n’apparaît pas dans le programme et personne ne peut s’y inscrire.',
  'publierAteliers.bandeau.publie': 'Publié{lexWorkshopAgr} : visible dans le programme',
  'publierAteliers.bouton': 'Publier maintenant',
  'publierAteliers.confirmer':
    'Publier « {titre} » maintenant ? {lexWorkshopPronounCap} apparaîtra dans le programme et les visiteurs pourront s’y inscrire.',
  'publierAteliers.toast.publie': '{lexWorkshopCap} publié{lexWorkshopAgr} : visible dans le programme.',
  'publierAteliers.erreur': 'La publication n’a pas abouti. Réessayez dans un instant.',

  // ── Étape « Publier maintenant ? » de la création ───────────────────────
  'publierAteliers.choix.titre': 'Publier maintenant ?',
  'publierAteliers.choix.texte':
    '{n, plural, one {Publié{lexWorkshopAgr}, votre {lexWorkshopBare} apparaît tout de suite dans le programme et les visiteurs peuvent s’y inscrire. En brouillon, {lexWorkshopPronoun} reste invisible du public : vous pourrez publier {lexWorkshopDem} plus tard depuis « Mes {lexWorkshopPlural} ».} other {Publié{lexWorkshopAgrPlural}, vos {lexWorkshopPlural} apparaissent tout de suite dans le programme et les visiteurs peuvent s’y inscrire. En brouillon, {lexWorkshopDefPlural} restent invisibles du public : vous pourrez les publier plus tard depuis « Mes {lexWorkshopPlural} ».}}',
  'publierAteliers.choix.publier': 'Publier maintenant',
  'publierAteliers.choix.brouillon': 'Garder en brouillon',
  'publierAteliers.creation.continuer': 'Continuer',
  'publierAteliers.creation.retour': 'Revenir au formulaire',
  'publierAteliers.toast.creeBrouillon':
    '{lexWorkshopCap} enregistré{lexWorkshopAgr} en brouillon : invisible du public pour le moment.',

  // ── Inscription animateur : quatrième étape ─────────────────────────────
  'exhibitorWizard.hostStep.publish': 'Publication',
  'exhibitorWizard.hostStepIntro.publish':
    'Dernière étape : choisissez si vos {lexWorkshopPlural} apparaissent dès maintenant dans le programme.',

  // ── Inscription animateur : succès selon le choix ───────────────────────
  'publierAteliers.succes.publie':
    '{n, plural, one {Votre {lexWorkshopBare} est publié{lexWorkshopAgr} : {lexWorkshopPronoun} apparaît dans le programme.} other {Vos {lexWorkshopPlural} sont publié{lexWorkshopAgrPlural} : visibles dans le programme.}}',
  'publierAteliers.succes.brouillon':
    '{n, plural, one {Votre {lexWorkshopBare} reste en brouillon, invisible du public.} other {Vos {lexWorkshopPlural} restent en brouillon, invisibles du public.}} Vous pourrez publier depuis « Mes {lexWorkshopPlural} ».',

  // ── Rappel sur l'accueil de l'espace animateur ──────────────────────────
  'publierAteliers.rappel':
    '{n, plural, one {# {lexWorkshopBare} n’est pas encore publié{lexWorkshopAgr}} other {# {lexWorkshopPlural} ne sont pas encore publié{lexWorkshopAgrPlural}}}',
  'publierAteliers.rappel.lien': 'Ouvrir mes {lexWorkshopPlural}',

  // ── Relance depuis le back-office ───────────────────────────────────────
  'relancePublication.bouton': 'Relancer les animateurs des {lexWorkshopPlural} non publié{lexWorkshopAgrPlural}',
  'relancePublication.titre': 'Relance des {lexWorkshopPlural} non publié{lexWorkshopAgrPlural}',
  'relancePublication.intro':
    'Chaque animateur reçoit un seul courriel, avec la liste de ses {lexWorkshopPlural} en brouillon et la marche à suivre pour les publier. Une même personne n’est relancée qu’une fois par jour, sauf si vous le confirmez.',
  'relancePublication.chargement': 'Recherche des {lexWorkshopPlural} en brouillon…',
  'relancePublication.erreur': 'Impossible de préparer la relance.',
  'relancePublication.vide': 'Aucun animateur à relancer : tous les {lexWorkshopPlural} rattachés sont publiés.',
  'relancePublication.destinataires': 'Animateurs à relancer ({n})',
  'relancePublication.inclure': 'Relancer {nom}',
  'relancePublication.deja': 'Déjà relancé aujourd’hui à {heure}',
  'relancePublication.forcer': 'Relancer quand même',
  'relancePublication.apercu': 'Voir le courriel',
  'relancePublication.apercu.titre': 'Courriel envoyé à {nom}',
  'relancePublication.apercu.fermer': 'Fermer l’aperçu',
  'relancePublication.apercu.erreur': 'Impossible d’afficher le courriel.',
  'relancePublication.sansAnimateur': 'Sans animateur, à traiter à la main ({n})',
  'relancePublication.sansAnimateur.aide':
    'Aucun animateur n’est rattaché {lexWorkshopAtPlural} suivants : rattachez-en un, ou publiez-les vous-même.',
  'relancePublication.nonJoignables': 'Comptes non joignables ({n})',
  'relancePublication.raison.profil': 'compte sans espace animateur',
  'relancePublication.raison.adresse': 'adresse électronique absente ou invalide',
  'relancePublication.raison.compte': 'compte {statut}',
  'relancePublication.statut.pending': 'en attente de validation',
  'relancePublication.statut.blocked': 'bloqué',
  'relancePublication.statut.archived': 'archivé',
  'relancePublication.archives': 'Archivés par l’organisation, non relancés ({n})',
  'relancePublication.copies': 'Copie de chaque courriel à : {adresses}',
  'relancePublication.copies.aucune': 'Aucune adresse en copie (réglage de l’écran « Codes d’accès »).',
  'relancePublication.compteur':
    '{n, plural, =0 {Aucun courriel ne partira} one {# courriel partira} other {# courriels partiront}}',
  'relancePublication.envoyer': 'Envoyer la relance',
  'relancePublication.confirmer':
    '{n, plural, one {# courriel va partir} other {# courriels vont partir}}{s, plural, =0 {} one {, dont # seconde relance aujourd’hui} other {, dont # secondes relances aujourd’hui}}. Confirmer l’envoi ?',
  'relancePublication.resultat':
    '{envoyes, plural, =0 {Aucun courriel envoyé} one {# courriel envoyé} other {# courriels envoyés}}{echecs, plural, =0 {} one {, # échec} other {, # échecs}}.',
  'relancePublication.envoiErreur': 'L’envoi n’a pas abouti.',
};
